/* eslint-disable max-lines */
import { Flex } from '../layout-components';
import ContentTableRow from './TableRow';
import TableSearch from './TableSearch';
import { TablePagination } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import { makeStyles } from '@material-ui/styles';
import { getColor } from 'common/constants/_colors';
import { SIZES } from 'common/constants/sizes';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Text from 'components/base/Text';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import TableHeaderCell, { EXPAND_TYPES } from './TableHeaderCell';
import useTablePersistExpandedRows from './useTablePersistExpandedRows';
import TableLoading from './TableLoading';
import { StyledTableHeaderRow } from './Table.styles';
import { boolPropsToClassNames } from 'common/utils/style.utils';

const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc'
};

const TableWrapper = styled.div`
  ${props => Object.values(props.classes)}
`;

const StyledSpan = styled.span`
  font-size: ${SIZES.large};
`;

const PaginationMessageWrapper = styled.div`
  margin-top: -1rem;
  margin-bottom: 1rem;
`;

const getPaginatedData = ({ tableData, page, rowsPerPage }) => {
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + Number(rowsPerPage);
  return tableData.slice(startIndex, endIndex);
};

const getColumnsWithExpanded = columns =>
  columns.reduce((acc, col) => {
    const isExpandedOpen = _.get(col, 'expandProps.isOpen', false);
    const exapndedCols = _.get(col, 'expandProps.columns', []);

    return [...acc, col, ...(isExpandedOpen ? exapndedCols : [])];
  }, []);

// eslint-disable-next-line max-lines-per-function
function WrappedTable({
  isLoading = false,
  columns = [],
  data = [],
  sortBy = null,
  sortOrder = null,
  allowEmptyData = false,
  onSort = null,
  withPagination = false,
  clientSidePagination = false,
  summaryRow = null,
  rowsPerPage = 25,
  totalRows = data.length,
  page = 0,
  onChangePage,
  onChangeRowsPerPage,
  localSearchOptions = {
    initialSearchValue: '',
    enabled: false,
    placeholder: '',
    filterFn: item => item,
    borderProps: {
      b: `1px solid ${getColor('black', 0.23)}`
    }
  },
  bodyScrollOptions = {
    enabled: false
  },
  tableComponentProps = {},
  tableWrapperProps = {},
  withTableHead = true,
  lineHighlightOnHover = false,
  transitionProps = {},
  onRowClick,
  rowsPerPageOptions,
  defaultSortBy = { key: null, order: SORT_ORDER.DESC },
  isPaginationPositionBottom = false,
  isRowsExpandable = false,
  isRowsDefaultExpanded = false,
  paginationMessage,
  disableNextPageButton,
  EmptyDataPlaceHolder = null,
  persistExpandedRowsProps = {},
  withStickyHeader = true,
  dataCy = 'table'
}) {
  const columnsWithExpanded = getColumnsWithExpanded(columns);
  const {
    initialSearchValue,
    enabled: searchEnabled,
    filterFn,
    prepareData
  } = localSearchOptions;

  const [search, setSearch] = useState(initialSearchValue || '');
  const [filteredData, setFilteredData] = useState(data);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(rowsPerPage);
  const [localPage, setLocalPage] = useState(page);

  const [localSortBy, setLocalSortBy] = useState(defaultSortBy);

  const { isRowExpanded, onExpandRow } = useTablePersistExpandedRows(
    persistExpandedRowsProps
  );

  const isControlledSortBy = typeof onSort === 'function';

  const onLocalRowsPerPageChange = event =>
    setLocalRowsPerPage(event.target.value);
  const onLocalPageChange = (event, page) => setLocalPage(page);

  const onClickSortCol = col => {
    const colSortByKey = col.sortPoint || col.dataPoint;
    const sortByKey = isControlledSortBy ? sortBy : localSortBy.key;
    const sortOrderVal = isControlledSortBy ? sortOrder : localSortBy.order;

    const newOrder =
      sortByKey !== colSortByKey || sortOrderVal === SORT_ORDER.ASC
        ? SORT_ORDER.DESC
        : SORT_ORDER.ASC;

    if (isControlledSortBy) {
      onSort(colSortByKey, newOrder);
    } else {
      setLocalSortBy({ key: colSortByKey, order: newOrder });
    }
  };

  useEffect(() => {
    if (searchEnabled) {
      setFilteredData(filterFn(data, search));
    }
  }, [data, search, prepareData]);

  useEffect(() => {
    if (localPage !== 0 && clientSidePagination) {
      setLocalPage(0);
    }
  }, [search, totalRows, localRowsPerPage]);

  const bodyClasses = bodyScrollOptions.enabled
    ? makeStyles({
        root: bodyScrollOptions.bodyStyles
      })()
    : null;

  const defaultTransitionProps = {
    enabled: false,
    timeout: { enter: 500, appear: 500, exit: 800 },
    enter: true,
    exit: true,
    rowKey: null,
    className: 'linbtablerow'
  };

  const _transitionProps = _.merge(defaultTransitionProps, transitionProps);
  const _filteredData = searchEnabled ? prepareData(filteredData) : data;

  const clientSidePaginationEnabled = withPagination && clientSidePagination;

  const tableData = clientSidePaginationEnabled
    ? getPaginatedData({
        tableData: _filteredData,
        page: localPage,
        rowsPerPage: localRowsPerPage
      })
    : _filteredData;

  const localSortByFn = row => {
    const rowSortedCol = _.get(row, localSortBy.key);
    return typeof rowSortedCol === 'string'
      ? rowSortedCol.toLowerCase()
      : rowSortedCol;
  };

  const orderedTableData = localSortBy.key
    ? _.orderBy(tableData, [localSortByFn], [localSortBy.order])
    : tableData;

  const Pagination = () => (
    <Flex justify='flex-end'>
      <div>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions || [5, 10, 25]}
          count={
            clientSidePaginationEnabled ? _.size(_filteredData) : totalRows
          }
          rowsPerPage={clientSidePagination ? localRowsPerPage : rowsPerPage}
          page={clientSidePagination ? localPage : page}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            style: {
              fontSize: SIZES.large
            },
            native: true
          }}
          labelDisplayedRows={({ from, to, count }) => {
            const resolvedTo =
              count === -1 && data.length < rowsPerPage
                ? from + data.length - 1
                : to === -1
                ? count
                : to;
            return (
              <StyledSpan>{`${from}-${resolvedTo}${
                count === -1 ? '' : ` of ${count}`
              }`}</StyledSpan>
            );
          }}
          labelRowsPerPage={<StyledSpan>Rows per page:</StyledSpan>}
          onPageChange={clientSidePagination ? onLocalPageChange : onChangePage}
          onRowsPerPageChange={
            clientSidePagination
              ? onLocalRowsPerPageChange
              : onChangeRowsPerPage
          }
          component='div'
          nextIconButtonProps={
            disableNextPageButton ? { disabled: true } : undefined
          }
        />
        {paginationMessage && (
          <PaginationMessageWrapper>
            <Text size='small' color='shuttleGray' pl='2rem'>
              {paginationMessage}
            </Text>
          </PaginationMessageWrapper>
        )}
      </div>
    </Flex>
  );

  const Row = ({ row, rowKey, expandLevel = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(
      isRowExpanded(row) || isRowsDefaultExpanded
    );
    const toggleIsExpanded = () => {
      const newValue = !isExpanded;

      setIsExpanded(newValue);
      onExpandRow(newValue, row);
    };

    const styles = _.get(row, 'styles', {});
    row = _.omit(row, 'styles');

    const drawByDataPoint = columnsWithExpanded.every(
      ({ dataPoint }) => dataPoint
    );

    const RowChildren = ({ row }) => {
      const children = _.get(row, 'children', []);
      return children.map((childRow, idx) => {
        const childKey = `${rowKey}-${idx}-${expandLevel}`;
        return (
          <Row
            row={childRow}
            rowKey={rowKey}
            key={childKey}
            expandLevel={expandLevel + 1}
          />
        );
      });
    };

    const RowContent = [
      <ContentTableRow
        key={rowKey}
        row={row}
        columns={columnsWithExpanded}
        drawByDataPoint={drawByDataPoint}
        styles={styles}
        lineHighlightOnHover={lineHighlightOnHover}
        transitionProps={_transitionProps}
        onRowClick={onRowClick}
        sortBy={isControlledSortBy ? sortBy : localSortBy.key}
        isRowsExpandable={isRowsExpandable}
        isExpanded={isExpanded}
        toggleIsExpanded={toggleIsExpanded}
        expandLevel={expandLevel}
      />,
      ...(isExpanded ? RowChildren({ row }) : [])
    ];

    return _transitionProps.enabled ? (
      <CSSTransition
        key={rowKey}
        timeout={_transitionProps.timeout}
        classNames={_transitionProps.className}
        unmountOnExit
      >
        {RowContent}
      </CSSTransition>
    ) : (
      RowContent
    );
  };
  const table = (!!tableData.length || allowEmptyData) && (
    <Table component='div' {...tableComponentProps} data-cy={dataCy}>
      {withTableHead ? (
        <TableHead component='div'>
          {summaryRow || null}
          <StyledTableHeaderRow
            component='div'
            className={boolPropsToClassNames({ withStickyHeader })}
          >
            {columns.reduce((acc, column, idx) => {
              const expandProps = _.get(column, 'expandProps');
              const isExpandable = _.get(expandProps, 'columns', []).length > 0;
              const isExpanded = isExpandable && expandProps.isOpen;

              const expandedHeaderCells = isExpanded
                ? expandProps.columns.map((expandedCol, expandIdx) => (
                    <TableHeaderCell
                      key={`${idx}-${expandIdx}`}
                      column={{ ...expandedCol, parentCol: column }}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={() => onClickSortCol(expandedCol)}
                      expandType={EXPAND_TYPES.CHILD}
                    />
                  ))
                : [];

              return [
                ...acc,
                <TableHeaderCell
                  key={idx}
                  column={column}
                  sortBy={isControlledSortBy ? sortBy : localSortBy.key}
                  sortOrder={isControlledSortBy ? sortOrder : localSortBy.order}
                  onSort={() => onClickSortCol(column)}
                  expandType={isExpandable && EXPAND_TYPES.PARENT}
                />,
                ...expandedHeaderCells
              ];
            }, [])}
          </StyledTableHeaderRow>
        </TableHead>
      ) : null}
      <TableBody
        data-cy='repo-container'
        component={_transitionProps.enabled ? TransitionGroup : 'div'}
        {...(bodyScrollOptions.enabled
          ? {
              classes: {
                root: bodyClasses.root
              }
            }
          : {})}
      >
        {orderedTableData.map((row, idx) => {
          const rowKey =
            (_transitionProps.rowKey && row[_transitionProps.rowKey]) || idx;

          return <Row row={row} key={rowKey} rowKey={rowKey} />;
        })}
      </TableBody>
    </Table>
  );

  if (isLoading) return <TableLoading columns={columns} />;

  return (
    <>
      {!!withPagination && !isPaginationPositionBottom && <Pagination />}
      <TableSearch
        value={search}
        setValue={setSearch}
        {...localSearchOptions}
      />

      {tableComponentProps.stickyHeader ? (
        <TableContainer>{table}</TableContainer>
      ) : !_.isEmpty(tableWrapperProps) ? (
        <TableWrapper {...tableWrapperProps}>{table}</TableWrapper>
      ) : (
        table
      )}
      {!orderedTableData?.length && EmptyDataPlaceHolder ? (
        <EmptyDataPlaceHolder />
      ) : (
        <></>
      )}
      {!!withPagination && isPaginationPositionBottom && <Pagination />}
    </>
  );
}

WrappedTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  onSort: PropTypes.func,
  sortOrder: PropTypes.string,
  sortBy: PropTypes.string
};

export default WrappedTable;
